import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { LoaderCircle, SearchIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";

// `search`: the one-line search field (SearchBar, the workspace picker) and the quick-open
// palette; its parts read the variant from context.
type CommandVariant = "default" | "search";
const CommandVariantContext = React.createContext<CommandVariant>("default");

function Command({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof CommandPrimitive> & {
  variant?: CommandVariant;
}) {
  return (
    <CommandVariantContext.Provider value={variant}>
      <CommandPrimitive
        data-slot="command"
        className={cn(
          variant === "search"
            ? "bg-popover text-popover-foreground flex h-8 w-full flex-col overflow-hidden rounded-md box-border"
            : "bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md",
          className
        )}
        {...props}
      />
    </CommandVariantContext.Provider>
  );
}

function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  className,
  showCloseButton = true,
  variant = "default",
  commandClass,
  shouldFilter = true,
  commandValue,
  onCommandValueChange,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
  variant?: CommandVariant;
  commandClass?: string;
  shouldFilter?: boolean;
  commandValue?: string;
  onCommandValueChange?: (value: string) => void;
}) {
  if (variant === "search") {
    return (
      <Dialog {...props}>
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogContent
          className={cn("overflow-hidden p-0 z-10000", className)}
          showCloseButton={showCloseButton}
        >
          <Command
            variant="search"
            shouldFilter={shouldFilter}
            value={commandValue}
            onValueChange={onCommandValueChange}
            className={cn(
              "[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-8 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-8 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5",
              commandClass ? commandClass : ""
            )}
          >
            {children}
          </Command>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn("overflow-hidden p-0", className)}
        showCloseButton={showCloseButton}
      >
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    loading?: boolean;
    wrapperClassName?: string;
  }
>(({ className, loading, wrapperClassName, ...props }, ref) => {
  const search = React.useContext(CommandVariantContext) === "search";
  return (
    <div
      data-slot="command-input-wrapper"
      className={
        search
          ? cn(
              "group flex h-8 items-center gap-2 px-3 relative",
              wrapperClassName
            )
          : cn("flex h-9 items-center gap-2 border-b px-3", wrapperClassName)
      }
    >
      <SearchIcon
        className={
          search
            ? "size-4 shrink-0 text-muted-foreground group-hover:text-primary group-focus-within:text-primary"
            : "size-4 shrink-0 opacity-50"
        }
      />
      <CommandPrimitive.Input
        ref={ref}
        data-slot="command-input"
        className={cn(
          search
            ? "flex h-8 w-full rounded-md bg-transparent! py-1.5 text-sm leading-normal font-regular text-foreground placeholder:text-muted-foreground outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            : "flex h-8 w-full rounded-md bg-transparent py-1.5 text-sm leading-normal font-regular text-foreground placeholder:text-muted-foreground outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
      {loading && (
        <LoaderCircle className="size-4 opacity-50 absolute right-3 animate-spin" />
      )}
    </div>
  );
});
CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => {
  const search = React.useContext(CommandVariantContext) === "search";
  return (
    <CommandPrimitive.List
      ref={ref}
      data-slot="command-list"
      className={cn(
        search
          ? "scroll-py-1 overflow-x-hidden overflow-y-auto"
          : "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
        className
      )}
      {...props}
    />
  );
});
CommandList.displayName = "CommandList";

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium",
        className
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("bg-border -mx-1 h-px", className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  const search = React.useContext(CommandVariantContext) === "search";
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        search
          ? "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-2 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          : "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
